import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "./SidenavCollapse";
import {
  useMaterialUIController,
  setMiniSidenav,
} from "context";

function Sidenav({ color, brand, brandName }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:1200px)");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const textColor = "white";

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDropdownToggle = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    setMiniSidenav(dispatch, isMobile);
  }, [dispatch, isMobile]);

  useEffect(() => {
    setMobileOpen(false); // close drawer on route change
  }, [location.pathname]);

  const customRoutes = [
    {
      type: "collapse",
      name: "Dashboard",
      key: "dashboard",
      icon: <Icon fontSize="small">dashboard</Icon>,
      route: "/dashboard",
    },
    {
      type: "collapse",
      name: "Slider",
      key: "slider",
      icon: <Icon fontSize="small">slideshow</Icon>,
      route: "/dashboard/slider",
    },
    {
      type: "collapse",
      name: "Service Management",
      key: "service-management",
      icon: <Icon fontSize="small">home_repair_service</Icon>,
      collapse: [
        {
          name: "Service Type",
          key: "service-type",
          route: "/dashboard/services/service-type",
        },
        {
          name: "Services",
          key: "services",
          route: "/dashboard/services/list",
        },
      ],
    },
    {
      type: "collapse",
      name: "Category Management",
      key: "category-management",
      icon: <Icon fontSize="small">category</Icon>,
      collapse: [
        {
          name: "Category",
          key: "categories",
          route: "/dashboard/categories/list",
        },
        {
          name: "Sub Categories",
          key: "sub-categories",
          route: "/dashboard/categories/sub-categories",
        },
      ],
    },
    {
      type: "collapse",
      name: "Order Management",
      key: "order-management",
      icon: <Icon fontSize="small">list</Icon>,
      collapse: [
        {
          name: "Order Statuses",
          key: "order-statuses",
          route: "/dashboard/orders/order-statuses",
        },
        {
          name: "Orders",
          key: "orders",
          route: "/dashboard/orders/list",
        },
      ],
    },
    {
      type: "collapse",
      name: "Driver Management",
      key: "driver-management",
      icon: <Icon fontSize="small">group</Icon>,
      collapse: [
        {
          name: "Drivers",
          key: "drivers",
          route: "/dashboard/drivers/list",
        },
        {
          name: "Vehicles",
          key: "vehicles",
          route: "/dashboard/drivers/vehicles",
        },
      ],
    },
     {
      type: "collapse",
      name: "Customer Management",
      key: "customer-management",
      icon: <Icon fontSize="small">group</Icon>,
      collapse: [
        {
          name: "Customers",
          key: "customers",
          route: "/dashboard/customers/list",
        },
      ],
    },
    {
      type: "collapse",
      name: "Payout Management",
      key: "payout-management",
      icon: <Icon fontSize="small">payments</Icon>,
      collapse: [
        {
          name: "Payout",
          key: "payout",
          route: "/dashboard/payout/list",
        },
        {
          name: "Driver Settlement",
          key: "Driver-Settlement",
          route: "/dashboard/driverSettlement/list",
        },
       
      ],
    },
    {
      type: "collapse",
      name: "Pages",
      key: "pages",
      icon: <Icon fontSize="small">auto_stories_icon</Icon>,
      collapse: [
        {
          name: "Privacy Policy",
          key: "privacy-policy",
          route: "/dashboard/pages/privacy-policy",
        },
        {
          name: "Terms & Conditions",
          key: "terms-conditions",
          route: "/dashboard/pages/terms-conditions",
        },
        {
          name: "About Us",
          key: "about-us",
          route: "/dashboard/pages/about-us",
        },
        {
          name: "News",
          key: "news",
          route: "/dashboard/pages/news",
        },
        {
          name: "Faqs",
          key: "faqs",
          route: "/dashboard/pages/faqs",
        },
        
      ],
    },
    {
      type: "collapse",
      name: "Settings",
      key: "settings",
      icon: <Icon fontSize="small">settings</Icon>,
      collapse: [
        {
          name: "Custom Form",
          key: "custom-form",
          route: "/dashboard/settings/custom-form",
        },
        {
          name: "Boarding Screen",
          key: "boarding-screen",
          route: "/dashboard/settings/boarding-screen",
        },
        {
          name: "Tutorials",
          key: "tutorial",
          route: "/dashboard/settings/tutorial",
        },
        {
          name: "Notification",
          key: "notification",
          route: "/dashboard/settings/notification",
        },
         {
            name: "Admin",
            key: "Admin",
            route: "/dashboard/Admin/list",
            
          },
         {
            name: "Bullet",
            key: "Bullet",
            route: "/dashboard/Bullet/list",
            
          },
      ],
    },
  ];

  const renderRoutes = customRoutes.map(({ type, name, icon, key, route, collapse }) => {
    if (type === "collapse") {
      return collapse ? (
        <div key={key}>
          <SidenavCollapse
            name={name}
            icon={icon}
            active={collapse.some((item) => item.route === location.pathname)}
            open={openDropdown === key}
            onClick={() => handleDropdownToggle(key)}
            dark
          />
          {openDropdown === key && (
            <List sx={{ pl: 3 }}>
              {collapse.map((item) => (
                <NavLink key={item.key} to={item.route}>
                  <SidenavCollapse
                    name={item.name}
                    active={item.route === location.pathname}
                    nested
                    dark
                  />
                </NavLink>
              ))}
            </List>
          )}
        </div>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse
            name={name}
            icon={icon}
            active={route === location.pathname}
            dark
          />
        </NavLink>
      );
    }
    return null;
  });

  const sidenavContent = (
    <MDBox
      height="100%"
      pt={3}
      px={2}
      sx={{
        backgroundColor: "#020202ff", // Dark background
        color: textColor,
      }}
    >
      <MDBox  style={{display: "flex", alignItems: "center", justifyContent: "center"}} >
        <MDBox component={NavLink} to="/"   >
        
          {brand && <MDBox component="img" src={brand} alt="Brand" width="5rem" style={{backgroundColor: "red"}} mr={1} />}
          {/* <MDTypography variant="h6" color={textColor}>
            {brandName} 
          </MDTypography> */}
        </MDBox>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        )}
      </MDBox>
      <Divider sx={{ my: 2, backgroundColor: "#374151" }} />
      <List>{renderRoutes}</List>
    </MDBox>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{ position: "fixed", top: 16, left: 16, zIndex: 1301 }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            PaperProps={{
              sx: {
                width: 260,
                backgroundColor: "#111827",
                color: "white",
              },
            }}
          >
            {sidenavContent}
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          open
          PaperProps={{
            sx: {
              width: miniSidenav ? 80 : 260,
              backgroundColor: "#111827",
              color: "white",
              transition: "width 0.3s",
              overflowX: "hidden",
              whiteSpace: "nowrap",
            },
          }}
        >
          {sidenavContent}
        </Drawer>
      )}
    </>
  );
}

Sidenav.propTypes = {
  color: PropTypes.string,
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
};

export default Sidenav;
